import React from "react";
import { Box, CircularProgress, Dialog, Typography } from "@mui/material";

/**
 * Pre-interview countdown modal (mobile) — portrait layout matching the Figma:
 * a large ring up top (Get ready → 3 → 2 → 1 → All the Best!) and the animated
 * colored bar strip along the bottom.
 *
 * Replicates the desktop `CircularProgressCountDown` behaviour (same ring,
 * same 7 bars + colors, same alternating-height logic, same ~1.7s timing) but
 * rebuilt in portrait so it fits a phone modal.
 */

// The desktop `boxes` — same colors and alternating heights, scaled to a mobile
// bottom strip.
const BARS = [
  { color: "#FFF6E5", h1: 220, h2: 80 },
  { color: "#DEF7FC", h1: 50, h2: 150 },
  { color: "#DFD8FD", h1: 100, h2: 230 },
  { color: "#DEF7FC", h1: 200, h2: 80 },
  { color: "#FFF6E5", h1: 50, h2: 180 },
  { color: "#DEF7FC", h1: 220, h2: 110 },
  { color: "#DFD8FD", h1: 100, h2: 220 },
];

const STRIP_HEIGHT = 150;
const BAR_SCALE = STRIP_HEIGHT / 230; // desktop's tallest bar is 230

function getLabel(value) {
  if (value >= 90) return "Get ready";
  if (value === 0) return "All the Best!";
  return String(Math.round(value / 25)); // 75->3, 50->2, 25->1
}

function CountdownRing({ value }) {
  const isFinal = value === 0;
  const isNumber = value > 0 && value < 90;
  const size = isFinal ? 200 : 160;
  const thickness = 2;
  const radius = (size - thickness) / 2;

  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      {/* Dashed base ring */}
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#d3d3d3"
          strokeWidth={thickness}
          strokeDasharray="2, 8"
          style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
        />
      </svg>

      {/* Purple progress arc (hidden on the final "All the Best!" frame) */}
      {!isFinal && (
        <CircularProgress
          variant="determinate"
          value={value}
          size={size}
          thickness={1}
          sx={{ color: "tertiary.clr-300", position: "absolute", top: 0, left: 0 }}
        />
      )}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{ color: "tertiary.clr-300", fontWeight: isNumber ? 700 : 600 }}
          fontSize={isNumber ? 32 : 22}
        >
          {getLabel(value)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function InterviewCountdown({ open = false, onComplete = () => {} }) {
  const [progress, setProgress] = React.useState(100);
  const completedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) return undefined;

    completedRef.current = false;
    setProgress(100);

    const timer = setInterval(() => {
      setProgress((prev) => prev - 25);
    }, 1700);

    return () => clearInterval(timer);
  }, [open]);

  React.useEffect(() => {
    if (open && progress < 0 && !completedRef.current) {
      completedRef.current = true;
      onComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, open]);

  const showBars = progress > 0;

  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{
        sx: {
          m: 2,
          borderRadius: 3,
          overflow: "hidden",
          width: "min(88vw, 340px)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 440 }}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
          }}
        >
          <CountdownRing value={progress} />
        </Box>

        {showBars && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              height: STRIP_HEIGHT,
              overflow: "hidden",
            }}
          >
            {BARS.map((bar, i) => {
              const raw = Math.round(progress / 25) % 2 === 0 ? bar.h1 : bar.h2;
              return (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    bgcolor: bar.color,
                    height: Math.round(raw * BAR_SCALE),
                    transition: "height 1s ease",
                  }}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
